import { Box, Checkbox, IconButton, Typography } from "@mui/material";
import { Download as DownloadIcon } from "@mui/icons-material";
import { QRCodeSVG } from "qrcode.react";
import { useTranslation } from "react-i18next";
import { getVideoDownloadUrl } from "@/api/galleriesApi";
import { PROJECT_ACCENT_VAR } from "@/components/project/ProjectThemeProvider";

export interface GalleryVideo {
  id: string;
  thumbnail_url?: string | null;
  embed_url?: string | null;
  duration?: number | null;
  public_url?: string | null;
  created_at?: string | null;
}

interface GalleryVideoCardProps {
  video: GalleryVideo;
  autoplay: boolean;
  muted: boolean;
  qrEnabled: boolean;
  qrPosition: string;
  qrSize: string;
  allowDownload?: boolean;
  gallerySlug?: string;
  selectionMode?: boolean;
  selected?: boolean;
  onToggleSelect?: (videoId: string) => void;
  onClick?: (video: GalleryVideo) => void;
}

const QR_SIZE_MAP: Record<string, number> = {
  small: 64,
  medium: 96,
  large: 128,
};

const QR_POSITION_MAP: Record<string, Record<string, number | string>> = {
  "bottom-right": { bottom: 8, right: 8 },
  "bottom-left": { bottom: 8, left: 8 },
  "top-right": { top: 8, right: 8 },
  "top-left": { top: 8, left: 8 },
};

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export default function GalleryVideoCard({
  video,
  autoplay,
  muted,
  qrEnabled,
  qrPosition,
  qrSize,
  allowDownload = false,
  gallerySlug,
  selectionMode = false,
  selected = false,
  onToggleSelect,
  onClick,
}: GalleryVideoCardProps) {
  const { t } = useTranslation("gallery");
  const qrPixelSize = QR_SIZE_MAP[qrSize] || 96;
  const positionStyles = QR_POSITION_MAP[qrPosition] || QR_POSITION_MAP["bottom-right"];

  const handleClick = () => {
    if (selectionMode && onToggleSelect) {
      onToggleSelect(video.id);
    } else if (onClick) {
      onClick(video);
    } else if (video.public_url) {
      window.open(video.public_url, "_blank");
    }
  };

  const handleDownload = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (gallerySlug) {
      window.open(getVideoDownloadUrl(gallerySlug, video.id), "_blank");
    }
  };

  return (
    <Box
      onClick={handleClick}
      sx={{
        position: "relative",
        width: "100%",
        height: "100%",
        overflow: "hidden",
        cursor: "pointer",
        bgcolor: "#111",
        borderRadius: 1,
        border: selected
          ? `2px solid var(${PROJECT_ACCENT_VAR}, #3b82f6)`
          : "2px solid transparent",
        transition: "border-color 0.15s ease",
      }}
    >
      {/* Video or thumbnail */}
      {autoplay && video.embed_url ? (
        <Box
          component="iframe"
          src={`${video.embed_url}?autoplay=true&loop=true&muted=${muted}&preload=true&controls=false`}
          allow="autoplay; encrypted-media"
          title={`Video ${video.id}`}
          sx={{
            width: "100%",
            height: "100%",
            border: "none",
            pointerEvents: "none",
          }}
        />
      ) : (
        <Box
          component="img"
          loading="lazy"
          src={video.thumbnail_url || undefined}
          alt=""
          sx={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
          }}
        />
      )}

      {/* Duration badge */}
      {video.duration != null && (
        <Typography
          variant="caption"
          sx={{
            position: "absolute",
            bottom: 8,
            left: 8,
            bgcolor: "rgba(0,0,0,0.7)",
            color: "#fff",
            px: 0.75,
            py: 0.25,
            borderRadius: 0.5,
            fontSize: "0.7rem",
            lineHeight: 1.2,
          }}
        >
          {formatDuration(video.duration)}
        </Typography>
      )}

      {/* Download icon (top-right) */}
      {allowDownload && !selectionMode && (
        <IconButton
          onClick={handleDownload}
          size="small"
          sx={{
            position: "absolute",
            top: 4,
            right: 4,
            bgcolor: "rgba(0,0,0,0.5)",
            color: "#fff",
            "&:hover": {
              bgcolor: "rgba(0,0,0,0.7)",
              color: `var(${PROJECT_ACCENT_VAR}, #3b82f6)`,
            },
            width: 32,
            height: 32,
          }}
          aria-label={t("public.download")}
        >
          <DownloadIcon sx={{ fontSize: 18 }} />
        </IconButton>
      )}

      {/* Selection checkbox (top-left) */}
      {selectionMode && (
        <Checkbox
          checked={selected}
          onClick={(e) => e.stopPropagation()}
          onChange={() => onToggleSelect?.(video.id)}
          sx={{
            position: "absolute",
            top: 2,
            left: 2,
            color: "rgba(255,255,255,0.8)",
            "&.Mui-checked": { color: "primary.main" },
            bgcolor: "rgba(0,0,0,0.3)",
            borderRadius: 0.5,
            p: 0.5,
          }}
        />
      )}

      {/* QR Code overlay */}
      {qrEnabled && video.public_url && (
        <Box
          sx={{
            position: "absolute",
            ...positionStyles,
            bgcolor: "rgba(255, 255, 255, 0.9)",
            borderRadius: 1,
            p: 0.5,
            lineHeight: 0,
          }}
        >
          <QRCodeSVG
            value={video.public_url}
            size={qrPixelSize}
            level="M"
            includeMargin={false}
          />
        </Box>
      )}
    </Box>
  );
}
